import React from 'react';
import ReviewCard from '../feed/ReviewCard';

function CreatorProfileView({
  creatorProfile,
  creatorReviews,
  reviewFilter,
  setReviewFilter,
  subscriptions,
  savedReviewIds,
  onBack,
  onSubscribe,
  onUnsubscribe,
  onUpgrade,
  onPhotoClick,
  onSaveReview,
  onUnsaveReview,
  onViewCreator
}) {
  const isSubscribed = subscriptions.find(sub => sub.creator.user_id === creatorProfile.user_id);
  const subscription = subscriptions.find(sub => sub.creator.user_id === creatorProfile.user_id);

  const handleSubscribeToggle = () => {
    if (isSubscribed && subscription) {
      onUnsubscribe(subscription.subscription_id, creatorProfile.name, creatorProfile.user_id);
    } else {
      onSubscribe(creatorProfile.user_id, 'free');
    }
  };

  return (
    <div className="space-y-6" data-testid="creator-profile-view">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-primary font-semibold hover:underline"
        data-testid="back-to-feed-button"
      >
        <span>←</span> Back to Feed
      </button>

      {/* Creator Header Card */}
      <div className="bg-white rounded-card shadow-card p-6">
        <div className="flex items-start gap-4 mb-4">
          {creatorProfile.picture ? (
            <img
              src={creatorProfile.picture}
              alt={creatorProfile.name}
              className="w-20 h-20 rounded-full object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
              {creatorProfile.name?.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-2xl font-heading font-bold mb-1">{creatorProfile.name}</h2>
            {creatorProfile.bio && (
              <p className="text-gray-600 text-sm mb-3">{creatorProfile.bio}</p>
            )}
            <div className="flex gap-4 text-sm text-gray-600 mb-3">
              <span className="font-semibold">{creatorProfile.review_count || 0} <span className="font-normal">recommendations</span></span>
            </div>
          </div>
        </div>

        {/* Profile Description */}
        {creatorProfile.description && (
          <div className="mb-4 pt-4 border-t border-gray-200">
            <p className="text-gray-700 text-sm leading-relaxed">{creatorProfile.description}</p>
          </div>
        )}

        {/* Subscribe Button */}
        <button
          onClick={handleSubscribeToggle}
          className={`w-full py-3 px-4 rounded-button font-semibold transition-all ${
            isSubscribed
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-primary text-white hover:shadow-hover'
          }`}
          data-testid={`creator-subscribe-button-${creatorProfile.user_id}`}
        >
          {isSubscribed ? 'Subscribed' : 'Subscribe'}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b-2 border-gray-200">
        <button
          onClick={() => setReviewFilter('all')}
          className={`px-4 py-2 font-semibold transition-all ${
            reviewFilter === 'all'
              ? 'text-primary border-b-2 border-primary -mb-0.5'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="filter-all"
        >
          All
        </button>
        <button
          onClick={() => setReviewFilter('6')}
          className={`px-4 py-2 font-semibold transition-all ${
            reviewFilter === '6'
              ? 'text-primary border-b-2 border-primary -mb-0.5'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="filter-must-go"
        >
          🤩 Must Go
        </button>
        <button
          onClick={() => setReviewFilter('5')}
          className={`px-4 py-2 font-semibold transition-all ${
            reviewFilter === '5'
              ? 'text-primary border-b-2 border-primary -mb-0.5'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="filter-enjoyed"
        >
          😍 Highly Enjoyed
        </button>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {creatorReviews.locked ? (
          <div className="bg-white rounded-card shadow-card p-8 text-center">
            <div className="mb-4">
              <span className="text-6xl">🔒</span>
            </div>
            <h3 className="text-xl font-heading font-bold mb-2">Subscribe to Unlock</h3>
            <p className="text-gray-600 mb-6">
              Get access to {creatorReviews.total_count} curated recommendations from {creatorProfile.name}
            </p>
            
            <div className="space-y-3 max-w-sm mx-auto">
              {/* Free Tier Option */}
              <div className="bg-gray-50 border-2 border-gray-200 rounded-button p-4">
                <h4 className="font-semibold mb-1">Free Tier</h4>
                <p className="text-sm text-gray-600 mb-3">Preview content with limited access</p>
                <button
                  onClick={() => onSubscribe(creatorProfile.user_id, 'free')}
                  className="w-full bg-gray-200 text-gray-700 py-2 rounded-button font-semibold hover:bg-gray-300"
                >
                  Start Free
                </button>
              </div>

              {/* Premium Tier Option */}
              <div className="bg-primary/10 border-2 border-primary rounded-button p-4">
                <h4 className="font-semibold mb-1">Premium Tier</h4>
                <p className="text-sm text-gray-600 mb-1">Full access to all content</p>
                <p className="text-2xl font-bold text-primary mb-3">${creatorProfile.subscription_price || 7}/mo</p>
                <button
                  onClick={() => onUpgrade(creatorProfile.user_id, creatorProfile.name, creatorProfile.subscription_price || 7.0)}
                  className="w-full bg-primary text-white py-2 rounded-button font-semibold hover:shadow-hover"
                >
                  Subscribe Premium
                </button>
              </div>
            </div>
          </div>
        ) : creatorReviews.reviews && creatorReviews.reviews.length > 0 ? (
          creatorReviews.reviews
            .filter(review => reviewFilter === 'all' || review.rating === parseInt(reviewFilter))
            .map(review => (
              <ReviewCard
                key={review.review_id}
                review={review}
                onPhotoClick={(index) => onPhotoClick(review, index)}
                onSave={() => onSaveReview(review.review_id, review.restaurant.name)}
                onUnsave={() => onUnsaveReview(review.review_id, review.restaurant.name)}
                isSaved={savedReviewIds.has(review.review_id)}
                onViewCreator={onViewCreator}
                onUpgrade={(creatorId, creatorName) => onUpgrade(creatorId, creatorName, 7.0)}
              />
            ))
        ) : (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No reviews yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreatorProfileView;
